export const EXECUTIVE_HIGHGATE_PROPERTY_UNIVERSE = 412

export const BENCHMARK_METRIC_TABS = [
  { id: 'shareOfVoice', label: 'Share of Voice' },
  { id: 'engagementRate', label: 'Engagement Rate' },
  { id: 'followerGrowth', label: 'Follower Growth' },
]

export const BENCHMARK_STATUS_TABS = [
  { id: 'behind', label: 'Behind benchmark' },
  { id: 'ahead', label: 'At / above benchmark' },
]

const METRIC_META = {
  shareOfVoice: {
    key: 'shareOfVoice',
    label: 'Share of Voice',
    shortLabel: 'SOV',
    summaryLabel: 'SOV',
  },
  engagementRate: {
    key: 'engagementRate',
    label: 'Engagement rate',
    shortLabel: 'Engagement',
    summaryLabel: 'Engagement',
  },
  followerGrowth: {
    key: 'followerGrowth',
    label: 'Follower growth',
    shortLabel: 'Follower growth',
    summaryLabel: 'Follower growth',
  },
}

const COVERAGE_TOOLTIP =
  'Properties without a configured competitor set or valid benchmark data are excluded. They are not counted as zero.'

export const BENCHMARK_SEVERITY_BANDS = {
  significantlyBehind: {
    id: 'significantlyBehind',
    label: 'significantly behind',
    shortLabel: 'Significantly behind',
    threshold: -1.0,
    color: '#dc2626',
    className: 'competitiveBenchmarkSeverity--significant',
  },
  moderatelyBehind: {
    id: 'moderatelyBehind',
    label: 'moderately behind',
    shortLabel: 'Moderately behind',
    min: -1.0,
    max: -0.5,
    color: '#d97706',
    className: 'competitiveBenchmarkSeverity--moderate',
  },
  marginallyBehind: {
    id: 'marginallyBehind',
    label: 'marginally behind',
    shortLabel: 'Marginally behind',
    min: -0.5,
    max: 0,
    color: '#9ca3af',
    className: 'competitiveBenchmarkSeverity--marginal',
  },
}

export function classifyBenchmarkGapSeverity(gapValue) {
  const gap = Number(gapValue)
  if (!Number.isFinite(gap) || gap >= 0) return null
  if (gap <= BENCHMARK_SEVERITY_BANDS.significantlyBehind.threshold) {
    return BENCHMARK_SEVERITY_BANDS.significantlyBehind
  }
  if (gap <= BENCHMARK_SEVERITY_BANDS.moderatelyBehind.max) {
    return BENCHMARK_SEVERITY_BANDS.moderatelyBehind
  }
  return BENCHMARK_SEVERITY_BANDS.marginallyBehind
}

export function buildBenchmarkSeverityDistribution(properties, metricFilter = 'shareOfVoice') {
  const counts = {
    significantlyBehind: 0,
    moderatelyBehind: 0,
    marginallyBehind: 0,
  }

  properties.forEach((property) => {
    const metricGap = getPropertyBenchmarkGaps(property).find((gap) => gap.key === metricFilter)
    if (!metricGap?.isBehind) return
    const severity = classifyBenchmarkGapSeverity(metricGap.gap)
    if (severity?.id) counts[severity.id] += 1
  })

  return counts
}

export function formatBenchmarkSeveritySummary(distribution) {
  const parts = [
    distribution.significantlyBehind
      ? `${distribution.significantlyBehind} significantly behind`
      : null,
    distribution.moderatelyBehind ? `${distribution.moderatelyBehind} moderately behind` : null,
    distribution.marginallyBehind ? `${distribution.marginallyBehind} marginally behind` : null,
  ].filter(Boolean)

  return parts.join(', ')
}

export function scaleBenchmarkSeverityDistribution(distribution, scaleFactor) {
  const scale = Number.isFinite(scaleFactor) ? scaleFactor : 1
  return Object.fromEntries(
    Object.entries(distribution).map(([key, count]) => [key, Math.round(count * scale)])
  )
}

const normalizeDeficit = (value, benchmark) => {
  if (benchmark == null || benchmark === 0) return 0
  if (value >= benchmark) return 0
  return (benchmark - value) / Math.abs(benchmark)
}

const normalizeSurplus = (value, benchmark) => {
  if (benchmark == null || benchmark === 0) return 0
  if (value <= benchmark) return 0
  return (value - benchmark) / Math.abs(benchmark)
}

export function getPropertyBenchmarkGaps(property) {
  return Object.values(METRIC_META).map((meta) => {
    const metric = property.metrics?.[meta.key]
    if (!metric) return null

    const value = Number(metric.value)
    const benchmark = Number(metric.benchmark)
    const gap = Number((value - benchmark).toFixed(1))

    return {
      ...meta,
      value,
      benchmark,
      gap,
      isBehind: gap < 0,
      isAhead: gap >= 0,
      normalizedDeficit: normalizeDeficit(value, benchmark),
      normalizedSurplus: normalizeSurplus(value, benchmark),
    }
  }).filter(Boolean)
}

export function getPrimaryBenchmarkGap(property, preferAhead = false) {
  const gaps = getPropertyBenchmarkGaps(property)
  if (preferAhead) {
    const ahead = gaps.filter((gap) => gap.isAhead && gap.normalizedSurplus > 0)
    if (!ahead.length) return gaps.find((gap) => gap.isAhead) || null
    return ahead.sort((left, right) => right.normalizedSurplus - left.normalizedSurplus)[0]
  }

  const behind = gaps.filter((gap) => gap.isBehind)
  if (!behind.length) return null
  return behind.sort((left, right) => right.normalizedDeficit - left.normalizedDeficit)[0]
}

export function getAdditionalBenchmarkGapCount(property, primaryKey, preferAhead = false) {
  const gaps = getPropertyBenchmarkGaps(property)
  if (preferAhead) {
    return gaps.filter((gap) => gap.key !== primaryKey && gap.isAhead).length
  }
  return gaps.filter((gap) => gap.key !== primaryKey && gap.isBehind).length
}

export function formatBenchmarkGap(gapValue) {
  const sign = gapValue >= 0 ? '+' : '−'
  return `${sign}${Math.abs(gapValue).toFixed(1)} pts`
}

export function formatBenchmarkComparison(value, benchmark) {
  return `${Number(value).toFixed(1)}% vs ${Number(benchmark).toFixed(1)}% competitor benchmark`
}

export function formatAdditionalGapLabel(count) {
  if (!count) return null
  return `+${count} more gap${count === 1 ? '' : 's'}`
}

function buildListRow(property, primaryGap, additionalGapCount) {
  const severity = classifyBenchmarkGapSeverity(primaryGap.gap)

  return {
    id: property.id,
    propertyId: property.propertyId || property.id,
    name: property.name,
    brand: property.brand,
    metricKey: primaryGap.key,
    metricLabel: primaryGap.label,
    gap: primaryGap.gap,
    gapDisplay: formatBenchmarkGap(primaryGap.gap),
    comparison: formatBenchmarkComparison(primaryGap.value, primaryGap.benchmark),
    additionalGapLabel: formatAdditionalGapLabel(additionalGapCount),
    severity,
    compSetLabel: property.compSetMeta?.competitorCount
      ? `${property.compSetMeta.competitorCount} local competitor${property.compSetMeta.competitorCount === 1 ? '' : 's'}`
      : null,
    property,
  }
}

export function buildBenchmarkPropertyRows(
  properties,
  { statusTab = 'behind', metricFilter = 'shareOfVoice' } = {}
) {
  const preferAhead = statusTab === 'ahead'

  const rows = properties
    .map((property) => {
      const metricGap = getPropertyBenchmarkGaps(property).find((gap) => gap.key === metricFilter)
      if (!metricGap) return null
      if (preferAhead ? metricGap.isBehind : !metricGap.isBehind) return null
      return buildListRow(property, metricGap, 0)
    })
    .filter(Boolean)

  return rows.sort((left, right) => {
    if (preferAhead) {
      return Math.abs(right.gap) - Math.abs(left.gap)
    }
    return Math.abs(left.gap) - Math.abs(right.gap)
  })
}

export function buildBenchmarkAttentionSummary(properties, coverage, periodBadge, metricFilter = 'shareOfVoice') {
  const behindInSample = properties.filter((property) => {
    const metricGap = getPropertyBenchmarkGaps(property).find((gap) => gap.key === metricFilter)
    return metricGap?.isBehind
  })

  const sampleSize = Math.max(properties.length, 1)
  const portfolioScale = coverage.representedPropertyCount / sampleSize
  const behindCount = Math.round(behindInSample.length * portfolioScale)
  const severityDistribution = scaleBenchmarkSeverityDistribution(
    buildBenchmarkSeverityDistribution(properties, metricFilter),
    portfolioScale
  )
  const severitySummary = formatBenchmarkSeveritySummary(severityDistribution)

  const primaryGapCounts = {
    shareOfVoice: 0,
    engagementRate: 0,
    followerGrowth: 0,
  }

  behindInSample.forEach((property) => {
    const primaryGap = getPrimaryBenchmarkGap(property)
    if (primaryGap?.isBehind) {
      primaryGapCounts[primaryGap.key] += 1
    }
  })

  const scaledPrimaryGapCounts = Object.fromEntries(
    Object.entries(primaryGapCounts).map(([key, count]) => [
      key,
      Math.round(count * portfolioScale),
    ])
  )

  const primaryGapBreakdown = [
    scaledPrimaryGapCounts.shareOfVoice
      ? `${scaledPrimaryGapCounts.shareOfVoice} ${METRIC_META.shareOfVoice.summaryLabel}`
      : null,
    scaledPrimaryGapCounts.engagementRate
      ? `${scaledPrimaryGapCounts.engagementRate} ${METRIC_META.engagementRate.summaryLabel}`
      : null,
    scaledPrimaryGapCounts.followerGrowth
      ? `${scaledPrimaryGapCounts.followerGrowth} ${METRIC_META.followerGrowth.summaryLabel}`
      : null,
  ].filter(Boolean)

  return {
    behindCount,
    representedPropertyCount: coverage.representedPropertyCount,
    totalPropertyCount: coverage.totalPropertyCount,
    coverageStatus: coverage.statusLabel,
    coverageTooltip: COVERAGE_TOOLTIP,
    periodBadge,
    primaryGapBreakdown,
    primaryGapSummary: primaryGapBreakdown.join(' · '),
    severityDistribution,
    severitySummary,
  }
}

export function getScaledBenchmarkAttentionForMetric(properties, coverage, metricFilter = 'shareOfVoice') {
  const sampleSize = Math.max(properties.length, 1)
  const portfolioScale = coverage.representedPropertyCount / sampleSize
  const behindRows = buildBenchmarkPropertyRows(properties, { statusTab: 'behind', metricFilter })
  const behindCount = Math.round(behindRows.length * portfolioScale)
  const severityDistribution = scaleBenchmarkSeverityDistribution(
    buildBenchmarkSeverityDistribution(properties, metricFilter),
    portfolioScale
  )

  return {
    behindCount,
    severityDistribution,
    severitySummary: formatBenchmarkSeveritySummary(severityDistribution),
  }
}

export function buildCompetitiveBenchmarkingInsights({
  properties = [],
  coverage,
  availability,
  periodBadge,
  metricFilter = 'shareOfVoice',
}) {
  return {
    availability,
    coverage,
    properties,
    attention: buildBenchmarkAttentionSummary(properties, coverage, periodBadge, metricFilter),
    behind: buildBenchmarkPropertyRows(properties, { statusTab: 'behind', metricFilter: 'shareOfVoice' }),
    ahead: buildBenchmarkPropertyRows(properties, { statusTab: 'ahead', metricFilter: 'shareOfVoice' }),
  }
}

export function getBenchmarkMetricMeta(metricKey) {
  return METRIC_META[metricKey] || null
}

export function getBenchmarkCoverageLabel(coverage) {
  return `Based on ${coverage.representedPropertyCount} of ${coverage.totalPropertyCount} properties with valid competitor data`
}

export { COVERAGE_TOOLTIP, METRIC_META }
